import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTutorial } from '../contexts/TutorialContext';

const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

const makeSlideSvg = (index, title) => {
  const safeTitle = String(title || '').replace(/</g, '').replace(/>/g, '').slice(0, 60);
  const safeIndex = String(index || '').replace(/</g, '').replace(/>/g, '').slice(0, 3);
  const svg = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="900" height="560" viewBox="0 0 900 560">
  <defs>
    <linearGradient id="bg" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0" stop-color="#0b1e3d"/>
      <stop offset="0.6" stop-color="#153a6e"/>
      <stop offset="1" stop-color="#0b1e3d"/>
    </linearGradient>
  </defs>
  <rect width="900" height="560" rx="24" fill="url(#bg)"/>
  <circle cx="120" cy="110" r="56" fill="rgba(250,204,21,0.18)"/>
  <circle cx="790" cy="450" r="86" fill="rgba(250,204,21,0.10)"/>
  <text x="120" y="128" text-anchor="middle" font-family="Segoe UI, Tahoma, Geneva, Verdana, sans-serif" font-size="54" font-weight="900" fill="#facc15">${safeIndex}</text>
  <text x="450" y="280" text-anchor="middle" font-family="Segoe UI, Tahoma, Geneva, Verdana, sans-serif" font-size="34" font-weight="800" fill="#ffffff">${safeTitle || 'Onboarding'}</text>
  <text x="450" y="330" text-anchor="middle" font-family="Segoe UI, Tahoma, Geneva, Verdana, sans-serif" font-size="18" font-weight="600" fill="rgba(255,255,255,0.88)">Imagem ilustrativa (substitua por screenshot)</text>
</svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
};

const tutorialImage = (n) => `/tutorial/${n}.jpeg`;

const DEFAULT_ONBOARDING_SLIDES = [
  {
    title: 'Dica 1',
    body: 'Essa tela apresenta a configuração do Jira para o perfil QA/DEV. Nela, o usuário deve informar o e-mail cadastrado no Jira e vinculado aos projetos da empresa, permitindo a identificação correta das ações realizadas, como criação de bugs, subtarefas e comentários. Após o preenchimento, basta clicar em “Salvar” para validar a integração e garantir o funcionamento adequado.',
    imageSrc: tutorialImage(1),
  },
  {
    title: 'Dica 2',
    body: 'Essa tela é destinada ao perfil QA para criação e configuração de um ciclo de teste. Nela, o usuário define o nome do ciclo, pode adicionar uma descrição e uma pré-condição (opcionais), além de informar a história do Jira para possibilitar a criação e o vínculo de subtarefas e bugs. Também é possível vincular casos de teste ao ciclo, garantindo rastreabilidade entre testes e demandas do Jira e facilitando o acompanhamento das execuções.',
    imageSrc: tutorialImage(2),
  },
  {
    title: 'Dica 3',
    body: 'Essa tela permite ao perfil QA criar uma nova execução de teste. Nela, o usuário deve informar o nome da execução, adicionar uma descrição (opcional), selecionar o projeto desejado e escolher o ciclo de teste vinculado à história do Jira. Após o preenchimento, é possível cancelar ou criar a execução, iniciando o acompanhamento dos testes relacionados.',
    imageSrc: tutorialImage(3),
  },
  {
    title: 'Dica 4',
    body: 'Essa tela apresenta os casos de teste vinculados a um ciclo, exibindo o progresso geral da execução. Cada caso corresponde a uma subtarefa criada no Jira, permitindo rastreabilidade entre testes e demandas. Nela, o usuário pode alterar o status de cada caso de teste, registrar descrições, pré-condições, passos e evidências. A atualização do status reflete diretamente no acompanhamento do ciclo, garantindo controle e visibilidade da execução dos testes.',
    imageSrc: tutorialImage(4),
  },
  {
    title: 'Dica 5',
    body: 'Essa tela do Jira mostra uma execução de teste regressivo com suas subtarefas vinculadas. À medida que as subtarefas são concluídas, o progresso é atualizado automaticamente, refletindo o percentual de conclusão. Quando as subtarefas atingem o status concluído, o status da tarefa pai é atualizado automaticamente, garantindo visibilidade, rastreabilidade e acompanhamento em tempo real da execução dos testes.',
    imageSrc: tutorialImage(5),
  },
  {
    title: 'Dica 6',
    body: 'Essa tela apresenta a execução dos casos de teste dentro de um ciclo, permitindo ao perfil QA acompanhar o progresso e alterar o status de cada caso. Nela, é possível marcar o teste como planejado, executando, concluído, bug ou melhoria, além de registrar descrição, pré-condições, passos de teste e evidências. A seleção do status bug ou melhoria possibilita o registro e rastreabilidade das ocorrências, garantindo integração e acompanhamento adequado no Jira.',
    imageSrc: tutorialImage(6),
  },
  {
    title: 'Dica 7',
    body: 'Essa tela permite ao perfil QA realizar a criação de um bug durante a execução dos testes. Nela, o usuário informa o título do bug, preenche a descrição obrigatória com os detalhes e passos para reprodução, visualiza a história do Jira vinculada ao ciclo e pode anexar evidências, se necessário. Após o preenchimento, basta clicar em “Criar Bug” para registrar a ocorrência e integrá-la automaticamente ao Jira.',
    imageSrc: tutorialImage(7),
  },
  {
    title: 'Dica 8',
    body: 'Essa tela do Jira mostra o bug criado e automaticamente vinculado à história do Jira. O item aparece na seção de tickets vinculados, garantindo rastreabilidade direta entre o caso de teste, o bug identificado e a história correspondente, facilitando o acompanhamento, a priorização e a correção pela equipe de desenvolvimento.',
    imageSrc: tutorialImage(8),
  },
  {
    title: 'Dica 9',
    body: 'Essa tela do Jira – Backlog mostra que os bugs identificados durante a execução dos testes são criados automaticamente no backlog do projeto. Dessa forma, as ocorrências ficam visíveis para todo o time, permitindo priorização, acompanhamento e tratamento dentro do fluxo ágil, mantendo a rastreabilidade entre testes, bugs e entregas.',
    imageSrc: tutorialImage(9),
  },
  {
    title: 'Dica 10',
    body: 'Essa tela apresenta o painel de Bugs/Melhorias de Casos de Teste, onde são listados os itens criados automaticamente durante a execução dos testes. Nela, é possível visualizar o ID do bug ou melhoria, o caso de teste relacionado, o tipo (bug ou melhoria), a descrição, o responsável, o status, a data de abertura e a última atualização. A opção “Detalhar” permite acessar mais informações do item, garantindo controle, rastreabilidade e acompanhamento completo das ocorrências identificadas.',
    imageSrc: tutorialImage(10),
  },
  {
    title: 'Dica 11',
    body: 'Essa tela apresenta o detalhamento de um bug ou melhoria identificado durante os testes. Nela, o QA acompanha e atualiza o status conforme o andamento da correção pelo time de DEV, mantendo o fluxo de validação. O painel também exibe o histórico completo de movimentações, garantindo rastreabilidade das alterações. Além disso, QA, DEV e Gerencial podem adicionar comentários, facilitando a comunicação, o alinhamento e o acompanhamento do item até sua resolução.',
    imageSrc: tutorialImage(11),
  },
];

const FinishOnboardingModal = ({ open, slides, onFinish }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [zoomOpen, setZoomOpen] = useState(false);

  const normalizedSlides = useMemo(() => {
    const list = Array.isArray(slides) ? slides : [];
    return list.map((s, idx) => ({
      ...s,
      imageSrc: String(s?.imageSrc || '') || makeSlideSvg(idx + 1, s?.title),
      title: String(s?.title || ''),
      body: String(s?.body || ''),
    }));
  }, [slides]);

  useEffect(() => {
    if (open) setCurrentStep(0);
  }, [open]);

  if (!open) return null;

  const total = normalizedSlides.length;
  const safeIndex = Math.max(0, Math.min(Number(currentStep) || 0, Math.max(0, total - 1)));
  const slide = normalizedSlides[safeIndex] || normalizedSlides[0];
  const isLast = safeIndex >= total - 1;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100000,
        background: 'rgba(0,0,0,0.65)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 18,
        pointerEvents: 'auto',
        fontFamily: 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
      }}
    >
      <div
        style={{
          width: 'min(1240px, 98vw)',
          height: 'min(780px, 92vh)',
          background: '#0b1e3d',
          borderRadius: 16,
          boxShadow: '0 22px 64px rgba(0,0,0,0.55)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div style={{ padding: 18, borderBottom: '1px solid rgba(255,255,255,0.10)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ fontWeight: 900, color: '#fff', fontSize: 16, letterSpacing: 0.2 }}>Guia rápido (Onboarding)</div>
          <div style={{ color: 'rgba(255,255,255,0.80)', fontSize: 13, fontWeight: 700 }}>{safeIndex + 1} / {total}</div>
        </div>

        <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1.7fr 1fr', gap: 0 }}>
          <div
            style={{ padding: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.06)' }}
            onClick={() => setZoomOpen(true)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') setZoomOpen(true);
            }}
          >
            <img
              src={slide?.imageSrc}
              alt={slide?.title || 'Onboarding'}
              onError={(e) => {
                try {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = makeSlideSvg(safeIndex + 1, slide?.title);
                } catch (_) {}
              }}
              style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: 14, display: 'block', cursor: 'zoom-in' }}
            />
          </div>

          <div style={{ padding: 20, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <div style={{ fontWeight: 900, fontSize: 22, color: '#fff', lineHeight: 1.15 }}>{slide?.title || ''}</div>
            <div style={{ marginTop: 12, fontSize: 14, lineHeight: 1.6, color: 'rgba(255,255,255,0.90)' }}>
              {slide?.body || ''}
            </div>
          </div>
        </div>

        <div style={{ padding: 16, borderTop: '1px solid rgba(255,255,255,0.10)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {normalizedSlides.map((_, idx) => (
              <button
                key={`dot-${idx}`}
                type="button"
                onClick={() => setCurrentStep(idx)}
                aria-label={`Ir para o slide ${idx + 1}`}
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: 999,
                  border: 'none',
                  background: idx === safeIndex ? '#facc15' : 'rgba(255,255,255,0.20)',
                  cursor: 'pointer',
                  padding: 0,
                }}
              />
            ))}
          </div>

          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            {safeIndex > 0 && (
              <button
                type="button"
                onClick={() => setCurrentStep((i) => Math.max(0, (Number(i) || 0) - 1))}
                style={{
                  border: 'none',
                  background: 'rgba(255,255,255,0.12)',
                  color: '#fff',
                  borderRadius: 10,
                  padding: '10px 14px',
                  cursor: 'pointer',
                  fontWeight: 900,
                }}
              >
                Voltar
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                if (isLast) {
                  onFinish && onFinish();
                  return;
                }
                setCurrentStep((i) => Math.min((Number(i) || 0) + 1, Math.max(0, total - 1)));
              }}
              style={{
                border: 'none',
                background: '#facc15',
                color: '#0b1e3d',
                borderRadius: 10,
                padding: '10px 16px',
                cursor: 'pointer',
                fontWeight: 900,
              }}
            >
              {isLast ? 'Finalizar' : 'Próximo'}
            </button>
          </div>
        </div>
      </div>

      {zoomOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 100001,
            background: 'rgba(0,0,0,0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 18,
          }}
          onClick={() => setZoomOpen(false)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Escape' || e.key === 'Enter' || e.key === ' ') setZoomOpen(false);
          }}
        >
          <img
            src={slide?.imageSrc}
            alt={slide?.title || 'Onboarding'}
            style={{ maxWidth: '96vw', maxHeight: '92vh', objectFit: 'contain', borderRadius: 14, display: 'block', cursor: 'zoom-out' }}
            onClick={(e) => {
              try { e.stopPropagation(); } catch (_) {}
              setZoomOpen(false);
            }}
            onError={(e) => {
              try {
                e.currentTarget.onerror = null;
                e.currentTarget.src = makeSlideSvg(safeIndex + 1, slide?.title);
              } catch (_) {}
            }}
          />
        </div>
      )}
    </div>
  );
};

const TourOverlay = () => {
  const tutorial = useTutorial();
  const navigate = useNavigate();
  const location = useLocation();
  const [rect, setRect] = useState(null);
  const [missing, setMissing] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [tooltipSize, setTooltipSize] = useState({ width: 360, height: 220 });
  const [fieldStatus, setFieldStatus] = useState({ title: false, description: false, preconditions: false });
  const [blockedFill, setBlockedFill] = useState(false);
  const lastTargetRef = useRef('');
  const lastChangeTargetRef = useRef('');
  const lastScrollRef = useRef('');
  const lastFieldsTargetRef = useRef('');
  const saveNextOnceRef = useRef('');
  const tooltipRef = useRef(null);
  const stepIndexRef = useRef(0);
  const lastModalAdvanceRef = useRef('');

  const active = Boolean(tutorial?.isTutorialMode);
  const step = tutorial?.currentStep;
  const isFinishStep = Boolean(active && step?.id === 'finish');

  useEffect(() => {
    stepIndexRef.current = tutorial?.stepIndex ?? 0;
  }, [tutorial?.stepIndex]);

  useEffect(() => {
    if (!active || !step?.route) return;
    if (location?.pathname !== step.route) {
      navigate(step.route);
    }
  }, [active, step?.route, location?.pathname, navigate]);

  const fieldsStepConfig = useMemo(() => {
    if (!active || !step?.id) return null;
    if (step.id === 'repo-testcase-fields') {
      return {
        highlightSelector: '[data-tour="modal-caso-campos"]',
        rootSelector: '[data-tour="modal-caso-campos"]',
        anchorSelector: '[data-tour="modal-caso-campos"]',
        saveSelector: '[data-tour="modal-caso-salvar"]',
        actionLabel: 'Salvar e Fechar',
        requiredKeys: ['title', 'description', 'preconditions'],
        fields: {
          title: 'input[name="title"]',
          description: 'textarea[name="description"]',
          preconditions: 'textarea[name="preconditions"]',
        },
        labels: {
          title: 'Nome do Caso de Teste',
          description: 'Descrição',
          preconditions: 'Pré-condição',
        },
      };
    }
    if (step.id === 'repo-cycle-fields') {
      return {
        highlightSelector: '[data-tour="modal-ciclo-campos"]',
        rootSelector: '[data-tour="modal-ciclo-campos"]',
        anchorSelector: '[data-tour="modal-ciclo-campos"]',
        saveSelector: '[data-tour="modal-ciclo-salvar"]',
        actionLabel: 'Salvar e Fechar',
        requiredKeys: ['title', 'description', 'preconditions'],
        fields: {
          title: 'input[name="name"]',
          description: 'textarea[name="description"]',
          preconditions: '[name="preconditions"]',
        },
        labels: {
          title: 'Nome do Ciclo de Teste',
          description: 'Descrição',
          preconditions: 'Pré-condição',
        },
      };
    }
    if (step.id === 'exec-fields') {
      return {
        highlightSelector: '[data-tour="modal-exec-campos"]',
        rootSelector: '[data-tour="modal-exec-campos"]',
        anchorSelector: '[data-tour="modal-exec-campos"]',
        saveSelector: '[data-tour="criar-execucao"]',
        actionLabel: 'Criar Execução',
        requiredKeys: ['title', 'description', 'preconditions'],
        fields: {
          title: 'input[name="name"]',
          description: 'select[name="project_id"]',
          preconditions: 'select[name="cycle_id"]',
        },
        labels: {
          title: 'Nome da Execução',
          description: 'Projeto',
          preconditions: 'Ciclo',
        },
      };
    }
    return null;
  }, [active, step?.id]);

  const fieldsSelector = useMemo(() => {
    if (!fieldsStepConfig) return null;
    return fieldsStepConfig.highlightSelector || null;
  }, [fieldsStepConfig]);

  const saveSelector = useMemo(() => {
    if (!fieldsStepConfig) return null;
    return fieldsStepConfig.saveSelector || null;
  }, [fieldsStepConfig]);

  const stepSelector = useMemo(() => {
    if (!active || !step?.target) return null;
    return `[data-tour="${step.target}"]`;
  }, [active, step?.target]);

  const isFieldsStep = Boolean(fieldsStepConfig);
  const isFieldsReady = useMemo(() => {
    if (!isFieldsStep || !fieldsStepConfig) return false;
    const required = Array.isArray(fieldsStepConfig?.requiredKeys)
      ? fieldsStepConfig.requiredKeys
      : ['title', 'description', 'preconditions'];
    return required.every((k) => Boolean(fieldStatus?.[k]));
  }, [fieldStatus, fieldsStepConfig, isFieldsStep]);

  const selector = useMemo(() => {
    if (!active) return null;
    if (isFieldsStep) return isFieldsReady ? (saveSelector || stepSelector) : (fieldsSelector || stepSelector);
    return stepSelector;
  }, [active, isFieldsStep, isFieldsReady, fieldsSelector, saveSelector, stepSelector]);

  useEffect(() => {
    if (!active || !step?.id) return;

    const modalSelectorByStepId = {
      'repo-add-testcase': '[data-tour="modal-caso-campos"]',
      'repo-new-cycle': '[data-tour="modal-ciclo-campos"]',
      'exec-new': '[data-tour="modal-exec-campos"]',
      'exec-view-cases': '[data-tour="modal-exec-casos-lista"]',
    };

    const modalSelector = modalSelectorByStepId[step.id];
    if (!modalSelector) return;

    const key = `${step.id}::${String(tutorial?.stepIndex ?? '')}`;
    if (lastModalAdvanceRef.current === key) return;

    let observer = null;
    let timeoutId = null;
    let cancelled = false;
    const startedAt = Date.now();
    const maxWaitMs = 8000;

    const check = () => {
      if (cancelled) return;
      const currentKey = `${step.id}::${String(tutorial?.stepIndex ?? '')}`;
      if (currentKey !== key) {
        cancelled = true;
        return;
      }

      const modal = document.querySelector(modalSelector);
      if (modal) {
        lastModalAdvanceRef.current = key;
        try {
          setTimeout(() => tutorial?.next?.(), 0);
        } catch (_) {
          tutorial?.next?.();
        }
        cancelled = true;
        if (timeoutId) {
          try { clearTimeout(timeoutId); } catch (_) {}
        }
        if (observer) {
          try { observer.disconnect(); } catch (_) {}
        }
        return;
      }

      const elapsed = Date.now() - startedAt;
      if (elapsed < maxWaitMs) {
        timeoutId = setTimeout(check, 150);
      }
    };

    check();

    if (typeof MutationObserver !== 'undefined') {
      observer = new MutationObserver(() => {
        check();
      });
      try {
        observer.observe(document.body, { childList: true, subtree: true, attributes: true });
      } catch (_) {}
    }

    return () => {
      cancelled = true;
      if (timeoutId) {
        try { clearTimeout(timeoutId); } catch (_) {}
      }
      if (observer) {
        try { observer.disconnect(); } catch (_) {}
      }
    };
  }, [active, step?.id, tutorial]);

  useEffect(() => {
    if (!active || !selector) {
      setRect(null);
      setMissing(false);
      setWaiting(false);
      return;
    }

    let timeoutId = null;
    let rafId = null;
    let observer = null;
    let cancelled = false;
    const startedAt = Date.now();
    const maxWaitMs = 7000;
    const scrollKey = `${String(tutorial?.stepIndex ?? '')}::${selector}`;

    const update = () => {
      if (cancelled) return;
      const el = document.querySelector(selector);
      if (!el) {
        setRect(null);
        const elapsed = Date.now() - startedAt;
        if (elapsed < maxWaitMs) {
          setWaiting(true);
          setMissing(false);
        } else {
          setWaiting(false);
          setMissing(true);
        }
        return false;
      }

      setWaiting(false);
      setMissing(false);
      try {
        if (lastScrollRef.current !== scrollKey) {
          lastScrollRef.current = scrollKey;
          el.scrollIntoView({ block: 'center', inline: 'center', behavior: 'smooth' });
        }
      } catch (_) {}
      const r = el.getBoundingClientRect();
      setRect({
        top: r.top,
        left: r.left,
        width: r.width,
        height: r.height,
      });
      return true;
    };

    const schedule = () => {
      if (cancelled) return;
      const found = update();
      if (!found) {
        const elapsed = Date.now() - startedAt;
        const delay = elapsed < 1200 ? 90 : 220;
        timeoutId = setTimeout(schedule, delay);
      } else {
        rafId = requestAnimationFrame(update);
      }
    };

    timeoutId = setTimeout(schedule, 150);

    if (typeof MutationObserver !== 'undefined') {
      observer = new MutationObserver(() => {
        update();
      });
      try {
        observer.observe(document.body, { childList: true, subtree: true, attributes: true });
      } catch (_) {}
    }

    const onViewportChange = () => update();
    try {
      window.addEventListener('resize', onViewportChange);
      window.addEventListener('scroll', onViewportChange, true);
    } catch (_) {}

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
      if (rafId) cancelAnimationFrame(rafId);
      if (observer) {
        try { observer.disconnect(); } catch (_) {}
      }
      try {
        window.removeEventListener('resize', onViewportChange);
        window.removeEventListener('scroll', onViewportChange, true);
      } catch (_) {}
    };
  }, [active, selector, tutorial?.stepIndex, location?.pathname]);

  useEffect(() => {
    if (!active || !step?.advanceOnClick || !selector) return;
    if (step?.id === 'repo-add-testcase' || step?.id === 'repo-new-cycle' || step?.id === 'exec-new' || step?.id === 'exec-view-cases') {
      return;
    }
    const key = `${step?.id || ''}::${selector}`;
    if (lastTargetRef.current === key) return;

    let observer = null;
    let timeoutId = null;
    let cancelled = false;
    const startedAt = Date.now();

    const attach = () => {
      if (cancelled) return false;
      const el = document.querySelector(selector);
      if (!el) return false;

      if (lastTargetRef.current === key) return true;
      lastTargetRef.current = key;

      const handler = () => {
        try {
          setTimeout(() => {
            tutorial?.next?.();
          }, 0);
        } catch (_) {
          tutorial?.next?.();
        }
      };

      el.addEventListener('click', handler, { once: true });
      return true;
    };

    const schedule = () => {
      if (cancelled) return;
      const attached = attach();
      const elapsed = Date.now() - startedAt;
      if (!attached && elapsed < 8000) {
        timeoutId = setTimeout(schedule, 250);
      }
    };

    schedule();

    if (typeof MutationObserver !== 'undefined') {
      observer = new MutationObserver(() => {
        attach();
      });
      try {
        observer.observe(document.body, { childList: true, subtree: true, attributes: true });
      } catch (_) {}
    }

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
      if (observer) {
        try { observer.disconnect(); } catch (_) {}
      }
    };
  }, [active, selector, step?.advanceOnClick, step?.id, tutorial]);

  useEffect(() => {
    if (!active || !step?.advanceOnChange || !selector) return;
    const key = `${step?.id || ''}::change::${selector}`;
    if (lastChangeTargetRef.current === key) return;

    let observer = null;
    let timeoutId = null;
    let cancelled = false;
    const startedAt = Date.now();

    const attach = () => {
      if (cancelled) return false;
      const el = document.querySelector(selector);
      if (!el) return false;

      if (lastChangeTargetRef.current === key) return true;
      lastChangeTargetRef.current = key;

      const handler = (e) => {
        if (e && e.isTrusted === true) {
          try {
            setTimeout(() => {
              tutorial?.next?.();
            }, 0);
          } catch (_) {
            tutorial?.next?.();
          }
        }
      };

      el.addEventListener('change', handler);
      return () => {
        try { el.removeEventListener('change', handler); } catch (_) {}
      };
    };

    let detach = null;
    const schedule = () => {
      if (cancelled) return;
      const result = attach();
      if (typeof result === 'function') detach = result;
      const elapsed = Date.now() - startedAt;
      if (!result && elapsed < 8000) {
        timeoutId = setTimeout(schedule, 200);
      }
    };

    schedule();

    if (typeof MutationObserver !== 'undefined') {
      observer = new MutationObserver(() => {
        const result = attach();
        if (typeof result === 'function') detach = result;
      });
      try {
        observer.observe(document.body, { childList: true, subtree: true, attributes: true });
      } catch (_) {}
    }

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
      if (observer) {
        try { observer.disconnect(); } catch (_) {}
      }
      if (typeof detach === 'function') {
        try { detach(); } catch (_) {}
      }
    };
  }, [active, selector, step?.advanceOnChange, step?.id, tutorial]);

  useEffect(() => {
    if (!active || !isFieldsStep || !fieldsStepConfig) {
      setFieldStatus({ title: false, description: false, preconditions: false });
      setBlockedFill(false);
      lastFieldsTargetRef.current = '';
      return;
    }

    const key = `${step?.id || ''}::fields::${String(fieldsSelector || '')}`;
    if (lastFieldsTargetRef.current === key) return;
    lastFieldsTargetRef.current = key;

    let observer = null;
    let timeoutId = null;
    let cancelled = false;
    const startedAt = Date.now();

    const resolveRoot = () => {
      if (fieldsStepConfig?.rootSelector) {
        const root = document.querySelector(fieldsStepConfig.rootSelector);
        if (root) return root;
      }
      if (fieldsStepConfig?.anchorSelector) {
        const anchor = document.querySelector(fieldsStepConfig.anchorSelector);
        if (anchor) return anchor.closest('form') || anchor.closest('.modal') || anchor.parentElement;
      }
      return null;
    };

    const compute = (root) => {
      const titleEl = root?.querySelector?.(fieldsStepConfig?.fields?.title || '');
      const descEl = root?.querySelector?.(fieldsStepConfig?.fields?.description || '');
      const preEl = root?.querySelector?.(fieldsStepConfig?.fields?.preconditions || '');
      const filled = (el) => String(el?.value || '').trim().length > 0;
      setFieldStatus({
        title: filled(titleEl),
        description: filled(descEl),
        preconditions: filled(preEl),
      });
      return { titleEl, descEl, preEl };
    };

    const attach = () => {
      if (cancelled) return null;
      const root = resolveRoot();
      if (!root) return null;

      const { titleEl, descEl, preEl } = compute(root);
      if (!titleEl || !descEl || !preEl) return null;
      const handler = () => compute(root);

      try { titleEl?.addEventListener?.('input', handler); } catch (_) {}
      try { descEl?.addEventListener?.('input', handler); } catch (_) {}
      try { preEl?.addEventListener?.('input', handler); } catch (_) {}
      try { titleEl?.addEventListener?.('change', handler); } catch (_) {}
      try { descEl?.addEventListener?.('change', handler); } catch (_) {}
      try { preEl?.addEventListener?.('change', handler); } catch (_) {}

      return () => {
        try { titleEl?.removeEventListener?.('input', handler); } catch (_) {}
        try { descEl?.removeEventListener?.('input', handler); } catch (_) {}
        try { preEl?.removeEventListener?.('input', handler); } catch (_) {}
        try { titleEl?.removeEventListener?.('change', handler); } catch (_) {}
        try { descEl?.removeEventListener?.('change', handler); } catch (_) {}
        try { preEl?.removeEventListener?.('change', handler); } catch (_) {}
      };
    };

    let detach = null;
    const schedule = () => {
      if (cancelled) return;
      if (detach) return;
      const result = attach();
      if (typeof result === 'function') detach = result;

      const elapsed = Date.now() - startedAt;
      if (!detach && elapsed < 8000) {
        timeoutId = setTimeout(schedule, 220);
      }
    };

    schedule();

    if (typeof MutationObserver !== 'undefined') {
      observer = new MutationObserver(() => {
        if (detach) return;
        const result = attach();
        if (typeof result === 'function') detach = result;
      });
      try {
        observer.observe(document.body, { childList: true, subtree: true, attributes: true });
      } catch (_) {}
    }

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
      if (observer) {
        try { observer.disconnect(); } catch (_) {}
      }
      if (typeof detach === 'function') {
        try { detach(); } catch (_) {}
      }
    };
  }, [active, isFieldsStep, fieldsStepConfig, fieldsSelector, step?.id]);

  useEffect(() => {
    if (!active || !isFieldsStep || !fieldsStepConfig) return;

    let intervalId = null;
    const filled = (el) => String(el?.value || '').trim().length > 0;

    const tick = () => {
      try {
        const root = (() => {
          if (fieldsStepConfig?.rootSelector) {
            const r = document.querySelector(fieldsStepConfig.rootSelector);
            if (r) return r;
          }
          if (fieldsStepConfig?.anchorSelector) {
            const a = document.querySelector(fieldsStepConfig.anchorSelector);
            if (a) return a.closest('form') || a.closest('.modal') || a.parentElement;
          }
          return null;
        })();
        if (!root) return;

        const titleEl = root?.querySelector?.(fieldsStepConfig?.fields?.title || '');
        const descEl = root?.querySelector?.(fieldsStepConfig?.fields?.description || '');
        const preEl = root?.querySelector?.(fieldsStepConfig?.fields?.preconditions || '');

        const next = {
          title: filled(titleEl),
          description: filled(descEl),
          preconditions: filled(preEl),
        };

        setFieldStatus((prev) => {
          if (
            Boolean(prev?.title) === Boolean(next.title) &&
            Boolean(prev?.description) === Boolean(next.description) &&
            Boolean(prev?.preconditions) === Boolean(next.preconditions)
          ) {
            return prev;
          }
          return next;
        });
      } catch (_) {}
    };

    tick();
    intervalId = setInterval(tick, 250);

    return () => {
      if (intervalId) {
        try { clearInterval(intervalId); } catch (_) {}
      }
    };
  }, [active, isFieldsStep, fieldsStepConfig]);

  useEffect(() => {
    if (!active || !isFieldsStep || !isFieldsReady) return;

    const key = `${String(tutorial?.stepIndex ?? '')}::${String(saveSelector || '[data-tour="modal-caso-salvar"]')}`;
    if (saveNextOnceRef.current !== key) {
      saveNextOnceRef.current = key;
    }

    let observer = null;
    let timeoutId = null;
    let cancelled = false;
    const startedAt = Date.now();

    const attach = () => {
      if (cancelled) return false;
      const el = document.querySelector(saveSelector || '[data-tour="modal-caso-salvar"]');
      if (!el) return false;

      const handler = () => {
        if (saveNextOnceRef.current === `${key}::clicked`) return;
        saveNextOnceRef.current = `${key}::clicked`;
        try {
          setTimeout(() => {
            if (tutorial?.currentStepId === step?.id) {
              tutorial?.next?.();
            }
          }, 0);
        } catch (_) {
          if (tutorial?.currentStepId === step?.id) {
            tutorial?.next?.();
          }
        }
      };

      try {
        el.addEventListener('click', handler, { once: true });
      } catch (_) {}

      return true;
    };

    const schedule = () => {
      if (cancelled) return;
      const ok = attach();
      const elapsed = Date.now() - startedAt;
      if (!ok && elapsed < 8000) {
        timeoutId = setTimeout(schedule, 200);
      }
    };

    schedule();

    if (typeof MutationObserver !== 'undefined') {
      observer = new MutationObserver(() => {
        attach();
      });
      try {
        observer.observe(document.body, { childList: true, subtree: true, attributes: true });
      } catch (_) {}
    }

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
      if (observer) {
        try { observer.disconnect(); } catch (_) {}
      }
    };
  }, [active, isFieldsStep, isFieldsReady, saveSelector, tutorial, step?.id]);

  useEffect(() => {
    if (!active || !isFieldsStep || isFieldsReady) {
      saveNextOnceRef.current = '';
    }
  }, [active, isFieldsStep, isFieldsReady]);

  useEffect(() => {
    if (!active || !step) return;
    const el = tooltipRef.current;
    if (!el) return;
    const measure = () => {
      try {
        const r = el.getBoundingClientRect();
        if (!r?.width || !r?.height) return;
        setTooltipSize({ width: r.width, height: r.height });
      } catch (_) {}
    };

    measure();
    let rafId = null;
    try {
      rafId = requestAnimationFrame(measure);
    } catch (_) {}
    return () => {
      if (rafId) {
        try { cancelAnimationFrame(rafId); } catch (_) {}
      }
    };
  }, [active, step, missing, waiting]);

  if (!active || !step) return null;

  if (isFinishStep) {
    return (
      <FinishOnboardingModal
        open
        slides={DEFAULT_ONBOARDING_SLIDES}
        onFinish={() => {
          try {
            navigate('/overview');
          } catch (_) {}
          try {
            setTimeout(() => tutorial?.finish?.(), 0);
          } catch (_) {
            tutorial?.finish?.();
          }
        }}
      />
    );
  }

  const handleNext = () => {
    if (isFieldsStep && fieldsStepConfig) {
      const required = Array.isArray(fieldsStepConfig?.requiredKeys)
        ? fieldsStepConfig.requiredKeys
        : ['title', 'description', 'preconditions'];
      const evaluateNow = () => {
        try {
          const root = (() => {
            if (fieldsStepConfig?.rootSelector) {
              const r = document.querySelector(fieldsStepConfig.rootSelector);
              if (r) return r;
            }
            if (fieldsStepConfig?.anchorSelector) {
              const a = document.querySelector(fieldsStepConfig.anchorSelector);
              if (a) return a.closest('form') || a.closest('.modal') || a.parentElement;
            }
            return null;
          })();
          const titleEl = root?.querySelector?.(fieldsStepConfig?.fields?.title || '');
          const descEl = root?.querySelector?.(fieldsStepConfig?.fields?.description || '');
          const preEl = root?.querySelector?.(fieldsStepConfig?.fields?.preconditions || '');
          const filled = (el) => String(el?.value || '').trim().length > 0;
          const nextStatus = {
            title: filled(titleEl),
            description: filled(descEl),
            preconditions: filled(preEl),
          };
          setFieldStatus(nextStatus);
          return required.every((k) => Boolean(nextStatus?.[k]));
        } catch (_) {
          return false;
        }
      };

      const readyNow = evaluateNow();
      if (!readyNow) {
        setBlockedFill(true);
        try {
          setTimeout(() => setBlockedFill(false), 1400);
        } catch (_) {}
      }
      return;
    }

    if (step?.id === 'repo-select-project') {
      const desired = String(tutorial?.tutorialProjectId || 'tutorial-project');
      const el = selector ? document.querySelector(selector) : null;
      const isSelect = el && typeof el === 'object' && String(el?.tagName || '').toUpperCase() === 'SELECT';
      if (isSelect) {
        const current = String(el.value || '');
        if (!current && desired) {
          try {
            el.value = desired;
            el.dispatchEvent(new Event('change', { bubbles: true }));
          } catch (_) {}
        }
        try {
          setTimeout(() => tutorial?.next?.(), 180);
        } catch (_) {
          tutorial?.next?.();
        }
        return;
      }
    }

    if (step?.advanceOnClick && selector) {
      const startIndex = stepIndexRef.current;
      const el = document.querySelector(selector);
      if (el) {
        try {
          el.click();
        } catch (_) {}
      }
      try {
        setTimeout(() => {
          if (stepIndexRef.current === startIndex) {
            tutorial?.next?.();
          }
        }, 700);
      } catch (_) {}
      return;
    }
    tutorial?.next?.();
  };

  const pad = 8;
  const highlight = rect
    ? {
        top: Math.max(0, rect.top - pad),
        left: Math.max(0, rect.left - pad),
        width: Math.max(0, rect.width + pad * 2),
        height: Math.max(0, rect.height + pad * 2),
      }
    : null;

  const tooltip = (() => {
    const margin = 14;
    const viewportW = typeof window !== 'undefined' ? window.innerWidth : 1200;
    const viewportH = typeof window !== 'undefined' ? window.innerHeight : 800;

    const w = Math.min(360, Math.max(280, Math.round(tooltipSize?.width || 360)));
    const h = Math.min(320, Math.max(160, Math.round(tooltipSize?.height || 220)));

    const clampBox = (box) => ({
      left: clamp(box.left, margin, Math.max(margin, viewportW - w - margin)),
      top: clamp(box.top, margin, Math.max(margin, viewportH - h - margin)),
      width: w,
    });

    if (!highlight) {
      if (waiting) return clampBox({ left: (viewportW - w) / 2, top: (viewportH - h) / 2 });
      return clampBox({ left: margin, top: margin });
    }

    const target = String(step?.target || '');
    const isModalTarget = target.startsWith('modal-');

    const candidates = isModalTarget
      ? [
          { left: highlight.left + highlight.width + 12, top: highlight.top },
          { left: highlight.left - w - 12, top: highlight.top },
          { left: highlight.left + highlight.width + 12, top: highlight.top + highlight.height - h },
          { left: highlight.left - w - 12, top: highlight.top + highlight.height - h },
          { left: highlight.left, top: highlight.top - h - 12 },
          { left: highlight.left + highlight.width - w, top: highlight.top - h - 12 },
          { left: highlight.left, top: highlight.top + highlight.height + 12 },
          { left: highlight.left + highlight.width - w, top: highlight.top + highlight.height + 12 },
          { left: margin, top: viewportH - h - margin },
          { left: margin, top: margin },
        ]
      : [
          { left: highlight.left, top: highlight.top + highlight.height + 12 },
          { left: highlight.left + highlight.width - w, top: highlight.top + highlight.height + 12 },
          { left: highlight.left, top: highlight.top - h - 12 },
          { left: highlight.left + highlight.width - w, top: highlight.top - h - 12 },
          { left: highlight.left + highlight.width + 12, top: highlight.top },
          { left: highlight.left - w - 12, top: highlight.top },
          { left: margin, top: margin },
          { left: margin, top: viewportH - h - margin },
        ];

    const overlapArea = (a, b) => {
      const x1 = Math.max(a.left, b.left);
      const y1 = Math.max(a.top, b.top);
      const x2 = Math.min(a.left + a.width, b.left + b.width);
      const y2 = Math.min(a.top + a.height, b.top + b.height);
      const iw = Math.max(0, x2 - x1);
      const ih = Math.max(0, y2 - y1);
      return iw * ih;
    };

    const highlightBox = {
      left: highlight.left,
      top: highlight.top,
      width: highlight.width,
      height: highlight.height,
    };

    let best = null;
    let bestScore = Number.POSITIVE_INFINITY;

    for (const raw of candidates) {
      const box = clampBox(raw);
      const test = { left: box.left, top: box.top, width: w, height: h };
      const overlap = overlapArea(test, highlightBox);
      const dx = (test.left + w / 2) - (highlightBox.left + highlightBox.width / 2);
      const dy = (test.top + h / 2) - (highlightBox.top + highlightBox.height / 2);
      const dist = Math.sqrt(dx * dx + dy * dy);
      const score = overlap * 10 + dist;
      if (score < bestScore) {
        bestScore = score;
        best = box;
      }
      if (overlap === 0) {
        best = box;
        break;
      }
    }

    return best || clampBox({ left: margin, top: margin });
  })();

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 99999,
        pointerEvents: 'none',
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: highlight ? 'transparent' : 'rgba(0,0,0,0.55)',
        }}
      />
      {highlight && (
        <>
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: highlight.top, background: 'rgba(0,0,0,0.55)' }} />
          <div style={{ position: 'absolute', top: highlight.top, left: 0, width: highlight.left, height: highlight.height, background: 'rgba(0,0,0,0.55)' }} />
          <div style={{ position: 'absolute', top: highlight.top, left: highlight.left + highlight.width, right: 0, height: highlight.height, background: 'rgba(0,0,0,0.55)' }} />
          <div style={{ position: 'absolute', top: highlight.top + highlight.height, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.55)' }} />
        </>
      )}
      {highlight && (
        <div
          style={{
            position: 'absolute',
            top: highlight.top,
            left: highlight.left,
            width: highlight.width,
            height: highlight.height,
            borderRadius: 10,
            boxShadow: blockedFill
              ? '0 0 0 3px rgba(248,113,113,0.95), 0 12px 32px rgba(0,0,0,0.45)'
              : '0 0 0 3px rgba(255,255,255,0.95), 0 12px 32px rgba(0,0,0,0.45)',
            background: 'transparent',
            pointerEvents: 'none',
          }}
        />
      )}
      <div
        style={{
          position: 'absolute',
          left: tooltip.left,
          top: tooltip.top,
          width: tooltip.width,
          background: '#0b1e3d',
          color: '#fff',
          borderRadius: 12,
          padding: 14,
          boxShadow: '0 14px 40px rgba(0,0,0,0.45)',
          pointerEvents: 'auto',
          fontFamily: 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
        }}
        ref={tooltipRef}
        onClick={(e) => {
          try { e.stopPropagation(); } catch (_) {}
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
          <div style={{ fontWeight: 800, fontSize: 14, lineHeight: 1.2 }}>{step.title || 'Tutorial'}</div>
          <button
            style={{
              border: 'none',
              background: 'rgba(255,255,255,0.14)',
              color: '#fff',
              borderRadius: 8,
              padding: '6px 10px',
              cursor: 'pointer',
            }}
            onClick={() => {
              navigate('/overview');
              setTimeout(() => tutorial?.finish?.(), 0);
            }}
          >
            Finalizar
          </button>
        </div>
        <div style={{ marginTop: 8, fontSize: 13, opacity: 0.95, lineHeight: 1.35 }}>
          {waiting
            ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10, padding: '10px 0' }}>
                <div
                  className="loading-spinner"
                  style={{
                    width: 22,
                    height: 22,
                    borderWidth: 3,
                    borderTopWidth: 3,
                    marginBottom: 0,
                  }}
                />
                <div style={{ fontWeight: 800 }}>Carregando…</div>
              </div>
            )
            : (missing
              ? 'Não encontrei o elemento desta etapa nesta tela. Você pode avançar ou voltar.'
              : (isFieldsStep
                ? (
                  <>
                    <div>Preencha os 3 campos:</div>
                    <div style={{ marginTop: 8, display: 'grid', gap: 4 }}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <span style={{ width: 16, textAlign: 'center', fontWeight: 900 }}>{fieldStatus?.title ? '✓' : '•'}</span>
                        <span>{fieldsStepConfig?.labels?.title || 'Nome'}</span>
                      </div>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <span style={{ width: 16, textAlign: 'center', fontWeight: 900 }}>{fieldStatus?.description ? '✓' : '•'}</span>
                        <span>{fieldsStepConfig?.labels?.description || 'Descrição'}</span>
                      </div>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <span style={{ width: 16, textAlign: 'center', fontWeight: 900 }}>{fieldStatus?.preconditions ? '✓' : '•'}</span>
                        <span>{fieldsStepConfig?.labels?.preconditions || 'Pré-condição'}</span>
                      </div>
                    </div>
                    <div style={{ marginTop: 10, padding: '8px 10px', borderRadius: 10, background: isFieldsReady ? 'rgba(34,197,94,0.18)' : 'rgba(255,255,255,0.10)' }}>
                      <div style={{ fontWeight: 800, color: isFieldsReady ? '#bbf7d0' : '#fde68a' }}>
                        {isFieldsReady ? `Pronto para ${String(fieldsStepConfig?.actionLabel || 'Salvar e Fechar').toLowerCase()}` : 'Complete os 3 campos para liberar o Avançar'}
                      </div>
                      <div style={{ marginTop: 4, opacity: 0.95 }}>
                        {isFieldsReady
                          ? `Agora clique em “${fieldsStepConfig?.actionLabel || 'Salvar e Fechar'}” (destacado) para continuar.`
                          : 'Se você tentar avançar sem preencher, eu vou reforçar o que falta.'}
                      </div>
                      {blockedFill && (
                        <div style={{ marginTop: 6, fontWeight: 800, color: '#fecaca' }}>
                          Preencha todos os 3 campos antes de avançar.
                        </div>
                      )}
                    </div>
                  </>
                )
                : step.body))}
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button
            style={{
              border: 'none',
              background: (isFieldsStep && isFieldsReady) ? 'rgba(255,255,255,0.14)' : '#22c55e',
              color: (isFieldsStep && isFieldsReady) ? '#fff' : '#052e16',
              borderRadius: 8,
              padding: '8px 12px',
              cursor: (isFieldsStep && isFieldsReady) ? 'not-allowed' : 'pointer',
              fontWeight: 800,
              opacity: (isFieldsStep && isFieldsReady) ? 0.7 : 1,
            }}
            onClick={(isFieldsStep && isFieldsReady) ? undefined : handleNext}
            disabled={Boolean(isFieldsStep && isFieldsReady)}
          >
            {(isFieldsStep && isFieldsReady) ? `Clique em “${fieldsStepConfig?.actionLabel || 'Salvar e Fechar'}”` : 'Avançar'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TourOverlay;
